import { Component, Inject, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectorRef, Input, Optional } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Editor, Toolbar } from 'ngx-editor';
import { map, Subject, takeUntil } from 'rxjs';
import { HouseEventService } from 'src/app/services/aws/house-and-well-being-events/house-event.service';
import { EditorHelperService } from 'src/app/services/shared/richText.service';
import { MatSelectChange } from '@angular/material/select';
import { ImageTagResponse } from 'src/app/services/aws/image-library/image-library';

interface EventImage {
  id?: string;
  filename?: string;
  name?: string;
  [key: string]: any;
}

@Component({
    selector: 'app-event-dialog',
    templateUrl: './event-dialog.component.html',
    styleUrls: ['./event-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EventDialogComponent implements OnInit, OnDestroy {
  eventForm: FormGroup;
  eventTitle: string = "Add New Event";
  editor: Editor;
  desc: string = "abc";
  characterCount: number = 0;
  showCharCount: boolean = false;
  maxCharacters: number = 1000;
  minStartDate: Date;
  minEndDate: Date;
  toolbar: Toolbar = [
    ['link'],
    ['background_color', 'text_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['ordered_list', 'bullet_list'],
    ['underline', 'italic', 'bold'],
  ];
  private destroy$ = new Subject();
  eventTypeImageMap = [];
  filteredImages: EventImage[] = [];
  private _eventImages: EventImage[] = [];
  get eventImages(): EventImage[] { return this._eventImages; }
  // For non-dialog usage
  @Input() set eventImages(value: EventImage[] | null | undefined) {
    this._eventImages = value ?? [];
    this.recomputeFilteredImages();
  }


  selectedImageForExpand: any = null;
  showImagePicker: boolean = false;
  imageSearchTerm: string = '';
  selectedCategory: string = 'all';
  categories: string[] = ['all'];
  tempSelectedImage: string = '';
  eventTags: ImageTagResponse[] = [];

  constructor(
    private fb: FormBuilder,
    private houseEventService: HouseEventService,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<EventDialogComponent>,
    private editorHelperService: EditorHelperService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { eventImages?: any[]; msgs?: any[];[key: string]: any } | null
  ) {
    this.eventForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      // description: ['', Validators.required],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      startTime: [null, Validators.required],
      endTime: [null, Validators.required],
      type: ['', [multiSelectRequired]],
      image: ['', Validators.required],
      registered: [0],
      capacity: ['', [Validators.required, naturalNumberValidator]],
      room: ['', Validators.required],
      content: ['', [richTextRequired]]
    });
  }

  ngOnInit(): void {
    this.editor = this.editorHelperService.createEditor();
    this.minStartDate = this.getTodayInEST();
    this.minEndDate = this.minStartDate;
    this.eventImages = this.data.msg;
    this.eventTags = this.data.msgTags;
    if (this.data) {
      const normalizedNames = this.normalizeType(this.data?.type);
      this.data.type = this.mapNamesToImageIds(normalizedNames, this.eventTags);
      const isEditable = !!this.data.isEditable;
      // setTimeout(() => {
      try {
        this.eventTitle = this.data.titleHeader;
        const patch = {
          name: Array.isArray(this.data.name) ? this.data.name[0] : this.data.name || '',
          content: typeof this.data.content === 'string' ? this.data.content : '<p></p>',
          startDate: this.data.startDate,
          startTime: Array.isArray(this.data.startTime) ? this.data.startTime[0] : this.data.startTime,
          endDate: this.data.endDate,
          endTime: Array.isArray(this.data.endTime) ? this.data.endTime[0] : this.data.endTime,
          capacity: Array.isArray(this.data.capacity) ? +this.data.capacity[0] : +this.data.capacity,
          room: Array.isArray(this.data.room) ? this.data.room[0] : this.data.room,
          type: this.data.type,
          image: this.data.fileName
        };
        this.eventForm.patchValue(patch);
        if (isEditable) {
          this.eventForm.get('name')?.disable();
        }

      } catch (err) {
        console.error('Patch error:', err);
      }
      // });
    }
    // if opened as a dialog and Input wasn’t used, initialize from MAT_DIALOG_DATA
    if (this._eventImages.length === 0 && this.data) {
      // Prefer eventImages if present; fall back to msgs for legacy dialog data structure.
      let fromDialog: any[] = [];
      if (Array.isArray(this.data.eventImages)) {
        fromDialog = this.data.eventImages;
      } else if (Array.isArray(this.data.msgs)) {
        fromDialog = this.data.msgs;
      }
      if (fromDialog.length) {
        this._eventImages = fromDialog;
        this.recomputeFilteredImages();
      }
    }
    this.buildCategories();
    ['startDate', 'startTime', 'endDate', 'endTime'].forEach(field => {
      this.eventForm.get(field)?.valueChanges.subscribe(() => this.validateDateTime());
    });

    this.eventForm.get('type')?.valueChanges.subscribe((selectedTypes: string[]) => {
      this.setDefaultImageForEventType(selectedTypes);
    });
  }

  private normalizeType(rawType: any): string[] {
    if (!rawType) return [];

    if (Array.isArray(rawType) && typeof rawType[0] === 'string') {
      return rawType[0]
        .split(',')
        .map(x => x.trim())
        .filter(Boolean);
    }

    if (typeof rawType === 'string') {
      return rawType
        .split(',')
        .map(x => x.trim())
        .filter(Boolean);
    }

    return [];
  }


  private mapNamesToImageIds(names: string[], allTags: any[]): number[] {
    if (!Array.isArray(names) || !Array.isArray(allTags)) return [];
    return allTags
      .filter(t => names.includes(t?.name))
      .map(t => t?.imageId)
      .filter(id => id != null);
  }

  toStringArray(input: unknown): string[] {
    const arr = Array.isArray(input) ? input : [input];
    return [...new Set(
      arr.reduce<string[]>((acc, v) => {
        if (typeof v === 'string') {
          acc.push(...v.split(','));
        }
        return acc;
      }, [])
        .map(s => s.trim())
        .filter(Boolean)
    )];
  }

  defaultImages: Record<string, string> = {
    Culinary: 'culinary-default.jpg',
    Fitness: 'fitness-default.jpg',
    Entertainment: 'ent-default.jpg',
    Social: 'social-default.jpg',
    Training: 'training-default.jpg'
  };

  onTypeChange(ev: MatSelectChange): void {
    const selectedTagImageIds: number[] = ev.value;
    if (!selectedTagImageIds || selectedTagImageIds.length !== 1) {
      this.eventForm.get('image')?.setValue('');
      return;
    }

    const selectedTagImageId = selectedTagImageIds[0];

    const selectedTag = this.eventTags.find(t =>
      Number(t.imageId) === Number(selectedTagImageId)
    );
    if (!selectedTag) {
      this.eventForm.get('image')?.setValue('');
      return;
    }

    const matchedImage = this.eventImages.find(img =>
      Number(img.id) === Number(selectedTag.imageId)
    );
    const keyToShow = matchedImage ? matchedImage.key : '';
    this.eventForm.get('image')?.setValue(keyToShow);
  }

  setDefaultImageForEventType(selectedTypes: string[]): void {
    if (selectedTypes && selectedTypes.length === 1) {
      // Only set default image when exactly one event type is selected
      const eventType = selectedTypes[0];
      const defaultImageId = this.eventTypeImageMap[eventType];

      if (defaultImageId) {
        this.eventForm.get('image')?.setValue(defaultImageId);
      }
    } else {
      // Clear the image if no event type is selected or multiple types are selected
      this.eventForm.get('image')?.setValue('');
    }
  }
  onNameInput(): void {
    const nameValue = this.eventForm.get('name')?.value || '';
    this.characterCount = nameValue.length;
    this.showCharCount = true;
  }


  ngOnDestroy(): void {
    this.editor?.destroy();
  }


  onSubmit(): void {
    if (this.eventForm.valid) {
      this.dialogRef.close(this.eventForm.value);
    }
  }
  onCancel(): void {
    this.dialogRef.close();
  }


  onContentChange(content: any) {

    // Strip HTML tags and count characters
    this.eventForm.get('content')?.setValue(content, { emitEvent: false });
    this.desc = this.eventForm.get('content').value[0];
    const textContent = content[0].replace(/<[^>]+>/g, '');

    this.characterCount = textContent.length;
  }

  wordCounter(event: KeyboardEvent) {
    const input = this.eventForm.get('content');
    const truncatedContent = this.eventForm.get('content')?.value?.replace(/<[^>]+>/g, '');

    if (input && truncatedContent.length >= this.maxCharacters && !this.isSpecialKey(event)) {
      event.preventDefault();
    }

  }
  private isSpecialKey(event: KeyboardEvent): boolean {

    const specialKeys = ["Backspace", "Delete", "ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"];
    return specialKeys.indexOf(event.key) !== -1;

  }

  disableScroll(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  combineDateAndTime(date: Date, time: string): Date | null {
    if (!date || !time) return null;
    const [hours, minutes] = time.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }
  // DST-aware EST/EDT support
  getNowInEST(): Date {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    const parts = new Intl.DateTimeFormat('en-US', options)
      .formatToParts(now)
      .reduce((acc: any, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});
    const formatted = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
    return new Date(formatted);
  }
  getTodayInEST(): Date {
    const now = this.getNowInEST();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  validateDateTime(): void {
    const startDate = this.eventForm.get('startDate')?.value;
    const startTime = this.eventForm.get('startTime')?.value;
    const endDate = this.eventForm.get('endDate')?.value;
    const endTime = this.eventForm.get('endTime')?.value;
    const startControl = this.eventForm.get('startTime');
    const endControl = this.eventForm.get('endTime');
    startControl?.setErrors(null);
    endControl?.setErrors(null);
    if (!startDate || !startTime || !endDate || !endTime) return;
    const start = this.combineDateAndTime(startDate, startTime);
    const end = this.combineDateAndTime(endDate, endTime);
    const nowEST = this.getNowInEST();
    if (start < nowEST) {
      startControl?.setErrors({ pastTime: true });
    }
    if (end < start) {
      endControl?.setErrors({ beforeStart: true });
    }
  }
  getSelectedImageName(): string {
    const selectedId = this.eventForm.get('image')?.value;
    if (!selectedId) return '';
    const selectedImage = this.eventImages.find(img => img.id === selectedId);
    return selectedImage ? selectedImage.fileName : '';
  }

  getSelectedImageUrl(): string {
    const selectedId = this.eventForm.get('image')?.value;
    if (!selectedId) return '';
    const selectedImage = this.eventImages.find(img => img.key === selectedId);
    return selectedImage ? selectedImage.content : '';
  }

  selectImage(imageId: string): void {
    this.eventForm.get('image')?.setValue(imageId);
  }

  expandImage(image: any, event: Event): void {
    event.stopPropagation();
    this.selectedImageForExpand = image;
  }

  closeExpandedImage(): void {
    this.selectedImageForExpand = null;
  }

  getSelectedImageColor(): string {
    const selectedId = this.eventForm.get('image')?.value;
    if (!selectedId) return '';
    const selectedImage = this.eventImages.find(img => img.id === selectedId);
    return selectedImage ? selectedImage.color : '';
  }

  expandSelectedImage(): void {
    const selectedId = this.eventForm.get('image')?.value;
    if (!selectedId) return;
    const selectedImage = this.eventImages.find(img => img.id === selectedId);
    if (selectedImage) {
      this.selectedImageForExpand = selectedImage;
    }
  }

  openImagePicker(): void {
    this.showImagePicker = true;
    this.tempSelectedImage = this.eventForm.get('image')?.value || '';
  }

  closeImagePicker(): void {
    this.showImagePicker = false;
    this.imageSearchTerm = '';
  }

  filterByCategory(category: string): void {
    this.selectedCategory = category;
  }


  buildCategories(): void {
    if (!this.eventImages?.length || !this.eventTags?.length) {
      this.categories = ['all'];
      return;
    }

    const tagMap = new Map<number, string>(
      this.eventTags.map(t => [
        t.id,
        (t.tagKey ?? '').trim().toLowerCase()
      ])
    );

    const unique: string[] = Array.from(
      new Set(
        this.eventImages
          .map(img => tagMap.get(img.tagId) ?? null) // allow null, not undefined
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
      )
    );
    this.categories = ['all', ...unique];
  }

  selectCategory(cat: string): void {
    this.selectedCategory = (cat || 'all').toLowerCase();
    this.recomputeFilteredImages();
  }


  private recomputeFilteredImages(): void {
    const images = this.eventImages ?? [];
    const selected = this.selectedCategory;
    // If "all", show all images
    if (selected === 'all') {
      this.filteredImages = images;
      return;
    }
    // Find tag entry by tagKey
    const tag = this.eventTags?.find(
      t => (t.tagKey ?? '').trim().toLowerCase() === selected
    );
    // If no tag found → no images
    if (!tag) {
      this.filteredImages = [];
      return;
    }
    // Filter images by tagId
    this.filteredImages = images.filter(img => img.tagId === tag.id);
  }

  trackByImg(index: number, img: EventImage): string {
    return img?.id ?? img.filename ?? img.name ?? index.toString();
  }

  getAvailableCategories(): string[] {
    // Get unique categories from event images that match event types
    const eventTypes = ['Culinary', 'Fitness', 'Entertainment', 'Signature Experiences'];
    const categories = [...new Set(this.eventImages.map(img => img.category))];
    return categories.filter(cat => eventTypes.includes(cat)).sort();
  }

  //   'Culinary': '1',        // Conference Room
  // 'Fitness': '2',     // Workshop Space
  // 'Entertainment': '3',      // Meeting Space
  // 'Signature Experiences': '4',   // Social Area

  selectImageFromPicker(imageId: string): void {
    this.tempSelectedImage = imageId;
  }

  isImageSelected(imageId: string): boolean {
    return this.tempSelectedImage === imageId;
  }

  confirmImageSelection(): void {
    this.eventForm.get('image')?.setValue(this.tempSelectedImage);
    this.closeImagePicker();
  }

  previewImage(image: any, event: Event): void {
    event.stopPropagation();
    this.selectedImageForExpand = image;
  }

  isDefaultImage(): boolean {
    const currentImageId = this.eventForm.get('image')?.value;
    const selectedTypes = this.eventForm.get('type')?.value || [];

    if (!currentImageId || selectedTypes.length === 0) return false;

    const primaryType = selectedTypes[0];
    return this.eventTypeImageMap[primaryType] === currentImageId;
  }

  getPrimaryEventType(): string {
    const selectedTypes = this.eventForm.get('type')?.value || [];
    return selectedTypes.length > 0 ? selectedTypes[0] : '';
  }
}


@Component({
    selector: 'app-dropdown-image-picker',
    templateUrl: './dropdown-image-picker.component.html',
    styleUrls: ['./event-dialog.component.scss'],
    standalone: false
})
export class DropdownImagePickerComponent extends EventDialogComponent {
  selectedImage: string = '';
  expandedImage: string = '';

  images = this.eventImages;

  onImageSelect(event: any) {
    this.selectedImage = event.target.value;
  }

  // getSelectedImageName(): string {
  //   const selected = this.images.find(img => img.content === this.selectedImage);
  //   return selected ? selected.fileName : '';
  // }

  getSelectedImageName(): string {
    const selectedId = this.eventForm.get('image')?.value;
    if (!selectedId) return '';
    const selectedImage = this.eventImages.find(img => img.id === selectedId);
    return selectedImage ? selectedImage.fileName : '';
  }

  expandSelectedImage(): void {
    this.expandedImage = this.selectedImage;
  }

  closeExpandedImage(): void {
    this.expandedImage = '';
  }
}

export function richTextRequired(control: AbstractControl): ValidationErrors | null {
  const value = control.value || '';
  const stripped = value.replace(/<[^>]*>/g, '').trim(); // remove all HTML
  return stripped.length === 0 ? { required: true } : null;
}

function naturalNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  return Number.isInteger(+value) && +value > 0 ? null : { naturalNumber: true };
}

function multiSelectRequired(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return value && value.length > 0 ? null : { required: true };
}